<script setup>
import { computed, nextTick, ref, useAttrs, useSlots, watch } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import EasyDataTable from 'vue3-easy-data-table';
import 'vue3-easy-data-table/dist/style.css';
import {
  DASHBOARD_EASY_DATA_TABLE_CLASS,
  DASHBOARD_EASY_DATA_TABLE_HEADER_CLASS,
} from './dashboardEasyDataTableTheme';

const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  rowsPerPage: {
    type: Number,
    default: 25,
  },
  rowsItems: {
    type: Array,
    default: () => [10, 25, 50, 100],
  },
  rowsPerPageMessage: {
    type: String,
    default: '',
  },
  emptyMessage: {
    type: String,
    default: '',
  },
  alternating: {
    type: Boolean,
    default: true,
  },
  themeColor: {
    type: String,
    default: '#2781f6',
  },
  /** Appended to shared dashboard table surface classes */
  extraTableClass: {
    type: String,
    default: '',
  },
  /** Appended to shared header classes */
  extraHeaderClass: {
    type: String,
    default: '',
  },
  /** When true, hide built-in footer if there are zero rows */
  hideFooterWhenEmpty: {
    type: Boolean,
    default: true,
  },
  /** When true, never show the library footer (use a custom footer + exposed pagination APIs). */
  hideBuiltInFooter: {
    type: Boolean,
    default: false,
  },
  /** Changing this resets client pagination to page 1 */
  paginationResetKey: {
    type: [String, Number],
    default: '',
  },
  /**
   * Sets vue3-easy-data-table `table-height` from the host flex area so the scroll body fills
   * available height when there are few rows. Intended for layouts with `hideBuiltInFooter`.
   */
  fillAvailableHeight: {
    type: Boolean,
    default: false,
  },
});

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const slots = useSlots();
const slotNames = computed(() => Object.keys(slots));

const tableRef = ref(null);
const tableHostEl = ref(null);
const fillHeightPx = ref(null);

useResizeObserver(tableHostEl, entries => {
  if (!props.fillAvailableHeight) return;
  const h = entries[0]?.contentRect?.height ?? 0;
  fillHeightPx.value = h > 1 ? Math.floor(h) : null;
});

const resolvedTableHeight = computed(() =>
  props.fillAvailableHeight ? fillHeightPx.value : null
);

const mergedTableClassName = computed(() =>
  [DASHBOARD_EASY_DATA_TABLE_CLASS, props.extraTableClass]
    .map(s => String(s || '').trim())
    .filter(Boolean)
    .join(' ')
);

const mergedHeaderClassName = computed(() =>
  [DASHBOARD_EASY_DATA_TABLE_HEADER_CLASS, props.extraHeaderClass]
    .map(s => String(s || '').trim())
    .filter(Boolean)
    .join(' ')
);

const hideFooter = computed(
  () =>
    props.hideBuiltInFooter ||
    (props.hideFooterWhenEmpty && !(props.items?.length > 0))
);

const resetToFirstPage = () => {
  nextTick(() => {
    tableRef.value?.updatePage?.(1);
  });
};

watch(
  () => [props.items?.length ?? 0, props.paginationResetKey],
  () => {
    resetToFirstPage();
  }
);

defineExpose({
  getTableRef: () => tableRef.value,
  updatePage: (...args) => tableRef.value?.updatePage?.(...args),
  nextPage: () => tableRef.value?.nextPage?.(),
  prevPage: () => tableRef.value?.prevPage?.(),
  updateRowsPerPageActiveOption: opt =>
    tableRef.value?.updateRowsPerPageActiveOption?.(opt),
});
</script>

<template>
  <div
    class="dashboard-easy-data-table-root flex min-h-0 w-full min-w-0 flex-1 basis-0 flex-col self-stretch"
  >
    <div ref="tableHostEl" class="relative h-0 min-h-0 flex-1 overflow-hidden">
      <EasyDataTable
        ref="tableRef"
        class="absolute inset-0 min-h-0 w-full min-w-0 max-w-full"
        v-bind="attrs"
        :headers="headers"
        :items="items"
        :rows-per-page="rowsPerPage"
        :rows-items="rowsItems"
        :rows-per-page-message="rowsPerPageMessage"
        :table-height="resolvedTableHeight ?? undefined"
        :hide-footer="hideFooter"
        :alternating="alternating"
        :theme-color="themeColor"
        :table-class-name="mergedTableClassName"
        :header-class-name="mergedHeaderClassName"
        :empty-message="emptyMessage"
      >
        <template v-for="name in slotNames" :key="name" #[name]="scope">
          <slot :name="name" v-bind="scope || {}" />
        </template>
      </EasyDataTable>
    </div>
  </div>
</template>
